// useActivityGeneralStore.js
// Store Pinia para o fluxo "Minhas Atividades" (ActivityGeneral).
//   – filtros aplicados no backend (debounce no componente + refetch)
//   – filteredActivities apenas repassa a página que o servidor devolveu
//   – setFilter/clearFilters só gravam; o componente observa filters e busca

import { defineStore } from "pinia";
import * as activityGeneralService from "@/services/activityGeneralService";

export const useActivityGeneralStore = defineStore("activityGeneral", {
  // ─────────────────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────────────────
  state: () => ({
    activities: [],
    selectedActivityIds: [],
    selectedActivity: null, // ✅ NOVO: atividade selecionada (card / edit / delete)
    error: null,

    totalCount: 0,
    totalPages: 1,
    currentPage: 1,
    pageSize: 10,

    // Filtros enviados ao backend (e espelhados no painel visual)
    filters: {
      activityId: "", // substring numérico
      textId: "", // substring numérico
      activityTitle: "", // substring textual
      textTitle: "", // substring textual

      // ✅ NOVO: destinatários de permissão da atividade
      permissionGroupName: "",
      permissionUserName: "",

      creatorId: "", // substring numérico
      creatorName: "", // substring textual
      startDateFrom: "", // ISO date string "YYYY-MM-DD"
      startDateTo: "", // ISO date string "YYYY-MM-DD"
      // ✅ MUDANÇA: progressMin/progressMax removidos; progress é substring de Activity.Status
      progress: "",
      onlyMine: false,
    },
  }),

  // ─────────────────────────────────────────────────────────────────────────
  // GETTERS
  // ─────────────────────────────────────────────────────────────────────────
  getters: {
    isAllSelected: (state) => (total) =>
      state.selectedActivityIds.length === total,

    /**
     * Passagem direta do que o backend devolveu — a filtragem é toda dele
     * (ActivityGeneralRepository aplica os filtros antes de paginar).
     *
     * Existia aqui um espelho client-side que repetia cada filtro em JS, para
     * pré-filtrar a página enquanto o debounce estava em voo. Ele custava mais
     * do que entregava: filtrava só a página carregada enquanto o rodapé mostra
     * o totalCount do servidor, então qualquer divergência entre as duas
     * implementações (acento, fuso na comparação de datas, campo ausente no
     * DTO) escondia linhas já recebidas e deixava a tela incoerente — tabela
     * vazia com "38 registros" no paginador. Mantido o nome do getter para não
     * mexer nos consumidores (mesmo padrão de filteredNotes).
     */
    filteredActivities: (state) => state.activities,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // ACTIONS
  // ─────────────────────────────────────────────────────────────────────────
  actions: {
    // ── Seleção ──────────────────────────────────────────────────────────
    toggleSelection(id) {
      const index = this.selectedActivityIds.indexOf(id);
      if (index > -1) {
        this.selectedActivityIds.splice(index, 1);
      } else {
        this.selectedActivityIds.push(id);
      }
    },

    selectAll(ids) {
      this.selectedActivityIds = [...ids];
    },

    clearSelection() {
      this.selectedActivityIds = [];
    },

    // ── Fetch paginado ────────────────────────────────────────────────────
    /**
     * Busca atividades paginadas no backend com os filtros atuais.
     * @param {number} pageNumber
     * @param {number} pageSize
     */
    async fetchActivitiesGeneralPaged(pageNumber, pageSize) {
      try {
        const filters = { ...this.filters };

        const response =
          await activityGeneralService.fetchActivitiesGeneralPaged(
            pageNumber,
            pageSize,
            filters,
          );

        this.activities = response.data.data;
        this.totalCount = response.data.totalCount;
        this.totalPages = response.data.totalPages;
        this.currentPage = pageNumber;
        this.error = null;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        this.error = { message };
        console.error(
          "❌ Erro ao buscar atividades gerais paginadas:",
          message,
        );
        throw new Error(message);
      }
    },

    // ── setFilter ─────────────────────────────────────────────────────────
    /**
     * Apenas grava o filtro. Quem busca é o componente, que observa
     * this.filters e volta à página 1 antes de chamar fetch — mesmo contrato
     * de useNoteGeneralStore. Buscar aqui dentro criava um segundo caminho
     * até a API, fora do guard de requisição concorrente do componente.
     * @param {string} field  - chave de this.filters
     * @param {string} value
     */
    setFilter(field, value) {
      if (!(field in this.filters)) return;
      this.filters[field] = value;
      // Espelha a volta à página 1 para as ações que recarregam a lista pela
      // própria store (deleteActivityById, updateActivity, …).
      this.currentPage = 1;
    },

    // ── clearFilters ──────────────────────────────────────────────────────
    /**
     * Limpa todos os filtros. Assim como setFilter, não busca: o componente
     * montado reage à mudança de this.filters; no teardown os watchers já
     * foram parados e nenhuma requisição sai.
     */
    clearFilters() {
      this.filters = {
        activityId: "",
        textId: "",
        activityTitle: "",
        textTitle: "",

        // ✅ NOVO: limpa os filtros por destinatários de permissão
        permissionGroupName: "",
        permissionUserName: "",

        creatorId: "",
        creatorName: "",
        startDateFrom: "",
        startDateTo: "",
        // ✅ MUDANÇA: progress substituiu progressMin/progressMax
        progress: "",
        onlyMine: false,
      };

      this.currentPage = 1;
    },

    // ── Delete unitário ───────────────────────────────────────────────────
    async deleteActivityById(id) {
      try {
        await activityGeneralService.deleteActivityGeneral(id);
        await this.fetchActivitiesGeneralPaged(this.currentPage, this.pageSize);
        // Se a página ficou vazia, regride uma página
        if (this.activities.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchActivitiesGeneralPaged(
            this.currentPage,
            this.pageSize,
          );
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir atividade:", message);
        throw new Error(message);
      }
    },

    // ── Delete em lote (seleção da store) ───────────────────────────────────
    async deleteSelectedActivities() {
      if (!this.selectedActivityIds.length) return;

      try {
        await activityGeneralService.deleteActivitiesGeneralBatch(
          this.selectedActivityIds,
        );
        this.clearSelection();
        await this.fetchActivitiesGeneralPaged(this.currentPage, this.pageSize);
        if (this.activities.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchActivitiesGeneralPaged(
            this.currentPage,
            this.pageSize,
          );
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao excluir atividades em lote:", message);
        throw new Error(message);
      }
    },

    // ✅ NOVO: delete em lote por IDs externos (elegíveis do batch modal)
    async deleteActivitiesByIds(ids) {
      if (!ids || !ids.length) return;
      try {
        await activityGeneralService.deleteActivitiesGeneralBatch(ids);
        // Remove da seleção os IDs deletados
        this.selectedActivityIds = this.selectedActivityIds.filter(
          (id) => !ids.includes(id),
        );
        await this.fetchActivitiesGeneralPaged(this.currentPage, this.pageSize);
        if (this.activities.length === 0 && this.currentPage > 1) {
          this.currentPage -= 1;
          await this.fetchActivitiesGeneralPaged(
            this.currentPage,
            this.pageSize,
          );
        }
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error(
          "❌ Erro ao excluir atividades em lote (by ids):",
          message,
        );
        throw new Error(message);
      }
    },

    // ✅ NOVO: busca atividade por ID e popula selectedActivity
    async getActivityById(id) {
      try {
        const response = await activityGeneralService.getActivityGeneralById(
          id,
        );
        this.selectedActivity = response.data;
        return response.data;
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao buscar atividade por ID:", message);
        throw new Error(message);
      }
    },

    // ✅ NOVO: atualiza atividade via PUT (mesmo endpoint do ActivityStore)
    async updateActivity(data) {
      try {
        await activityGeneralService.updateActivityGeneral(data);
        await this.fetchActivitiesGeneralPaged(this.currentPage, this.pageSize);
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        console.error("❌ Erro ao atualizar atividade geral:", message);
        throw new Error(message);
      }
    },
  },
});